import React, { useCallback, useEffect, useRef, useState } from "react";
import { Badge, Box, Button, CloseButton, HStack, Spinner, Text, VStack } from "@chakra-ui/react";
import { fetchWorkItem } from "../../services/workItemsService";
import WorkItemActions from "./WorkItemActions";
import WorkItemActivity, { formatStamp } from "./WorkItemActivity";
import PersonChip from "./PersonChip";
import { normalizeStatus, STATUS_META } from "../../utils/workItemStatus";
import { onWorkItemsChanged } from "../../utils/workItemsBus";
import { openMessages } from "../../utils/messagingBus";
import { loadCurrentStaffUser } from "../../utils/currentStaffUser";

const CARD_WIDTH = 320;
// Below this much room under the link (and more above it), the card opens above.
const MIN_ROOM_BELOW = 260;

// Plain YYYY-MM-DD → "Sep 30, 2026" without a UTC→local day shift.
export function formatDay(value) {
  const m = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return "";
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// The card behind a task or order link (a note's task link, a 📋 chip in a
// chat message): what it is, its status and its people for anyone who opens
// it; its actions, activity feed and "Open in Tasks" only for the people on
// it. The host positions it at the clicked link (`anchorRect`, relative to the
// host) and passes its width, and its height when the card should open above
// a link near the bottom (a chat's newest messages) instead of being cut off.
export default function WorkItemCard({
  kind = "task", id, schema, currentUserId, currentUserSchema, anchorRect, hostWidth, hostHeight, onClose,
}) {
  const [item, setItem] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showActivity, setShowActivity] = useState(false);
  const [commentFocus, setCommentFocus] = useState(0);
  // The clinic group Reassign searches: the host's, or the signed-in user's own.
  const [ownSchema, setOwnSchema] = useState(null);
  const cardRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const noun = kind === "order" ? "order" : "task";

  const load = useCallback(async () => {
    try {
      const res = await fetchWorkItem({ kind, id, schema });
      if (!res?.item) throw new Error("not found");
      setItem(res.item);
      setError(null);
    } catch (err) {
      setItem(null);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [kind, id, schema]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  useEffect(() => onWorkItemsChanged(() => load()), [load]);

  useEffect(() => {
    if (currentUserSchema) return undefined;
    let cancelled = false;
    loadCurrentStaffUser()
      .then((user) => { if (!cancelled && user?.schema) setOwnSchema(user.schema); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [currentUserSchema]);

  useEffect(() => {
    // Another window (the Reject/Reassign window, a toast) renders in its own
    // portal: working in it must not close the card. The window the card itself
    // sits in (the Messages modal is a portal too) is just "outside".
    const inOtherWindow = (el) => {
      const portal = el?.closest?.(".chakra-portal");
      return !!portal && !portal.contains(cardRef.current);
    };
    const onKey = (e) => {
      if (e.key !== "Escape" || inOtherWindow(document.activeElement)) return;
      // Capture phase, so the window around the card never sees this Escape:
      // it closes the card, not the whole Messages window.
      e.stopPropagation();
      onCloseRef.current?.();
    };
    const onDown = (e) => {
      if (inOtherWindow(e.target)) return;
      if (cardRef.current && !cardRef.current.contains(e.target)) onCloseRef.current?.();
    };
    document.addEventListener("keydown", onKey, true);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      document.removeEventListener("mousedown", onDown);
    };
  }, []);

  const rawLeft = anchorRect?.left || 0;
  const left = Math.max(0, hostWidth > 0 ? Math.min(rawLeft, hostWidth - (CARD_WIDTH + 8)) : rawLeft);
  const linkTop = anchorRect?.top ?? anchorRect?.bottom ?? 0;
  const roomBelow = hostHeight > 0 ? hostHeight - (anchorRect?.bottom || 0) - 6 : Infinity;
  const roomAbove = linkTop - 6;
  const above = roomBelow < MIN_ROOM_BELOW && roomAbove > roomBelow;
  const placement = above
    ? { bottom: `${hostHeight - linkTop + 6}px`, maxH: `${Math.max(160, roomAbove - 8)}px` }
    : { top: `${(anchorRect?.bottom || 0) + 6}px`, maxH: hostHeight > 0 ? `${Math.max(160, roomBelow - 8)}px` : "70vh" };
  const peopleSchema = currentUserSchema || ownSchema;

  let body;
  if (loading) {
    body = (
      <HStack py={4} justify="center">
        <Spinner size="sm" />
      </HStack>
    );
  } else if (error || !item) {
    body = (
      <Text fontSize="sm" color="gray.500" py={2}>
        This {noun} no longer exists
      </Text>
    );
  } else {
    const status = normalizeStatus(item.status);
    const meta = STATUS_META[status] || STATUS_META.unassigned;
    const involved = !!currentUserId && (item.assigned_to?.id === currentUserId || item.assigned_by?.id === currentUserId);
    const ref = {
      kind, id: item.id, schema: item.schema, status, title: item.title,
      assignedTo: item.assigned_to?.id || null, assignedBy: item.assigned_by?.id || null,
    };
    const fromNote = kind === "task"
      ? [item.patient_name, item.visit_date && `visit ${formatDay(item.visit_date)}`].filter(Boolean).join(" · ")
      : "";
    body = (
      <VStack align="stretch" spacing={1.5} fontSize="sm">
        <HStack spacing={2} pr={6} flexWrap="wrap">
          {kind === "order" && <Badge colorScheme="blue">{item.item_type === "followup" ? "Follow-up" : "Order"}</Badge>}
          <Badge colorScheme={meta.colorScheme} borderRadius="full" px={2} data-testid="work-item-card-status">
            {meta.label}
          </Badge>
        </HStack>
        <Text fontWeight="bold" fontSize="md">{item.title}</Text>
        {kind === "order" && item.patient_name && (
          <Text color="gray.600" _dark={{ color: "gray.300" }}>Patient: {item.patient_name}</Text>
        )}
        {item.description && (
          <Text whiteSpace="pre-wrap" color="gray.600" _dark={{ color: "gray.300" }}>{item.description}</Text>
        )}
        {item.assigned_to && <PersonChip label="Assigned to" person={item.assigned_to} />}
        {item.assigned_by && <PersonChip label="Assigned by" person={item.assigned_by} />}
        {item.due_date && (
          <Text>
            <Text as="span" color="gray.500">Due: </Text>
            {formatDay(item.due_date)}
          </Text>
        )}
        {kind === "order" && item.visit_date && (
          <Text>
            <Text as="span" color="gray.500">Visit: </Text>
            {formatDay(item.visit_date)}
          </Text>
        )}
        {item.created_at && <Text fontSize="xs" color="gray.500">Created {formatStamp(item.created_at)}</Text>}
        {item.submitted_at && <Text fontSize="xs" color="gray.500">Marked done {formatStamp(item.submitted_at)}</Text>}
        {item.completed_at && <Text fontSize="xs" color="gray.500">Completed {formatStamp(item.completed_at)}</Text>}
        {fromNote && <Text fontSize="xs" color="gray.500">From note: {fromNote}</Text>}
        {involved && (
          <>
            <Box pt={1}>
              <WorkItemActions item={ref} currentUserId={currentUserId} currentUserSchema={peopleSchema}
                               showStatus={false} onChanged={load}
                               onComment={() => {
                                 setShowActivity(true);
                                 setCommentFocus((n) => n + 1);
                               }} />
            </Box>
            <HStack justify="space-between" pt={1}>
              <Button size="xs" variant="link" colorScheme="blue" aria-expanded={showActivity}
                      onClick={() => setShowActivity((v) => !v)}>
                {showActivity ? "Hide activity" : "Activity"}
              </Button>
              <Button size="xs" variant="link" colorScheme="blue"
                      onClick={() => {
                        openMessages({ tab: "tasks", focus: { kind, id: item.id } });
                        onCloseRef.current?.();
                      }}>
                Open in Tasks
              </Button>
            </HStack>
            {showActivity && (
              <WorkItemActivity item={ref} currentUserId={currentUserId} maxH="200px" focusComment={commentFocus} />
            )}
          </>
        )}
      </VStack>
    );
  }

  return (
    <Box
      ref={cardRef}
      role="dialog"
      aria-label={kind === "order" ? "Order details" : "Task details"}
      className="task-link-card"
      position="absolute"
      {...placement}
      left={`${left}px`}
      w={`${CARD_WIDTH}px`}
      maxW="calc(100% - 8px)"
      overflowY="auto"
      zIndex={120}
      bg="white"
      _dark={{ bg: "gray.800", borderColor: "gray.600" }}
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="md"
      boxShadow="lg"
      p={3}
    >
      <CloseButton size="sm" position="absolute" top={1} right={1} aria-label={`Close ${noun} details`}
                   onClick={() => onClose?.()} />
      {body}
    </Box>
  );
}
